import React, { useEffect, Component } from "react";
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Toaster } from "sonner";
import "@/App.css";

// --- Notification Services ---
import { initNotificationService } from "./services/notificationService";

// --- Context & Layout Components ---
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PhoneShell from "./components/PhoneShell";
import ScrollToTop from "./components/ScrollToTop";
import BackButtonHandler from "./components/BackButtonHandler";

// --- Public Pages ---
import LandingPage from "./pages/LandingPage";
import Features from "./pages/Features";
import AboutUs from "./pages/AboutUs";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import SharePage from "./pages/SharePage";
import Help from "./pages/Help";
import RefundPolicy from "./pages/RefundPolicy";
import AccountDeletion from "./pages/AccountDeletion";

// --- Authentication Pages ---
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Verify from "./pages/Verify";

// --- Admin Pages ---
import AdminRoute from "./components/AdminRoute";
import AdminDashboard from "./pages/AdminDashboard";

// --- Protected App Pages ---
import UserDashboard from "./pages/UserDashboard"; 
import Capture from "./pages/Capture";
import LeadDetail from "./pages/LeadDetail";
import Profile from "./pages/Profile";
import Reminders from "./pages/Reminders";
import Payment from "./pages/Payment"; 
import Events from "./pages/Events";
import WhatsAppOnboarding from "./pages/WhatsAppOnboarding";

// Global Production Error Boundary
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Global ErrorBoundary caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-slate-50 font-sans">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-200 text-rose-600 flex items-center justify-center font-bold text-xl mb-4">
            !
          </div>
          <h2 className="text-base font-bold text-slate-900 mb-1">Something went wrong</h2>
          <p className="text-xs text-slate-500 mb-4 max-w-xs">
            The application encountered an unexpected state. Tap below to reload.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-xs font-bold shadow-xs hover:bg-blue-700 transition"
          >
            Reload Application
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Native Push Notification Listener & Foreground Sync
function NotificationListener() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // 1. Proactively request permissions and register channels on app boot
    initNotificationService();

    // 2. Handle notification tap deep-links safely
    let notifHandle = null;
    LocalNotifications.addListener(
      "localNotificationActionPerformed",
      (action) => {
        const rawLeadId = action.notification?.extra?.leadId;
        if (rawLeadId) {
          const cleanLeadId = String(rawLeadId).trim();
          // Security guard: Ensure leadId is an alphanumeric/UUID string to prevent path injection
          if (/^[a-zA-Z0-9_-]{1,64}$/.test(cleanLeadId)) {
            navigate(`/leads/${cleanLeadId}`);
          }
        }
      }
    ).then((handle) => {
      notifHandle = handle;
    });

    // 3. Keep channel active when app returns from background
    let appStateHandle = null;
    CapApp.addListener("appStateChange", (state) => {
      if (state.isActive) {
        initNotificationService();
      }
    }).then((handle) => {
      appStateHandle = handle;
    });

    return () => {
      if (notifHandle && typeof notifHandle.remove === "function") notifHandle.remove();
      if (appStateHandle && typeof appStateHandle.remove === "function") appStateHandle.remove();
    };
  }, [navigate]);

  return null;
}

function App() {
  const isNative = Capacitor.isNativePlatform();
  const AppRouter = isNative ? HashRouter : BrowserRouter;

  useEffect(() => {
    if (isNative) {
      // Delay splash screen hide until the browser paints the first frame
      requestAnimationFrame(() => {
        SplashScreen.hide().catch((err) => 
          console.warn("Splash Screen hiding notice:", err)
        );
      });
    }
  }, [isNative]);

  return (
    <ErrorBoundary>
      <div className="App">
        <AppRouter>
          <ScrollToTop />
          <BackButtonHandler />
          <NotificationListener />

          <AuthProvider>
            <Routes>
              {/* Root Entry: Native goes directly to Dashboard; Web to Landing Page */}
              <Route 
                path="/" 
                element={isNative ? <Navigate to="/dashboard" replace /> : <LandingPage />} 
              />

              {/* Marketing Pages (Web Only) */}
              {!isNative && (
                <>
                  <Route path="/about" element={<AboutUs />} />
                  <Route path="/features" element={<Features />} />
                </>
              )}

              {/* Legal, Support, & Compliance Pages (REQUIRED in both Native & Web for App Stores) */}
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/help" element={<Help />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/account-deletion" element={<AccountDeletion />} />

              {/* Public Visiting Card View */}
              <Route path="/share/:token" element={<SharePage />} />

              {/* Authentication Routes */}
              <Route path="/login" element={<Navigate to="/auth/login" replace />} />
              <Route path="/register" element={<Navigate to="/auth/register" replace />} />
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify" element={<Verify />} />
              
              {/* Protected Admin Routes */}
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminDashboard />} />
              </Route>

              {/* Protected User Dashboard Routes */}
              <Route element={<PhoneShell />}>
                <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />   
                <Route path="/leads/:id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
                <Route path="/capture" element={<ProtectedRoute><Capture /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
                <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
                <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
                <Route path="/whatsapp" element={<ProtectedRoute><WhatsAppOnboarding /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Routes>
            
            <Toaster 
              position="top-center" 
              richColors 
              closeButton 
              duration={3500} 
              visibleToasts={3}
            />
          </AuthProvider>
        </AppRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;